import { el, list, setChildren, mount } from 'redom';

import { isPropertyValueExists, filterByPropertyPartialValue } from 'utilities/collection';
import { findSelectedEntryIndex } from './utilities';

import NavigableOption from './option';

class NavigableList {

  constructor(options) {

    this.hoveredIndex = -1;
    this.working = false;

    this.class = options.class || null;
    this.entries = options.entries;
    this.selectedEntries = options.selectedEntries;
    this.displayedEntries = options.entries;
    this.OptionEntry = options.OptionEntry;
    this.noResultDisplay = options.noResultDisplay || el('li.no-results-found', 'No options available');
    this.onSelect = options.onSelect;
    this.onClose = options.onClose;
    this.scrollableParent = options.scrollableParent;
    this.scrollAnchorElement = options.scrollAnchorElement;
    this.multiselect = options.multiselect === true;
    this.minimumSelections = options.minimumSelections || 0;
    this.skipPositioning = options.skipPositioning === true;
    this.preventNoResultDisplay = options.preventNoResultDisplay || false;
    this.skipScrollingToFocus = options.skipScrollingToFocus || false;
    this.context = options.context || {};

    this.spinner = el('ht-spinner', { size: 'small' });
    this.el = list('ul.navigable-list-main.helptap-list.native.hide-scrollbar' + this.getClassName(), NavigableOption, 'key', {
      navigableList: this,
      onSelect: this.handleOptionSelect.bind(this),
      onUnselect: this.handleOptionUnselect.bind(this),
      onHighlight: this.handleOptionHighlight.bind(this),
      OptionEntry: this.OptionEntry
    });

    this.el.update(this.displayedEntries, this.context);

  }

  getClassName() {

    if (this.class === null) {
      return '';
    }

    return `.${this.class}`;

  }

  onmount() {}

  handleOptionUnselect(index) {

    if (this.multiselect === false) {
      this.handleOptionSelect(index);
      return;
    } else if (this.selectedEntries.length <= this.minimumSelections) {
      return;
    }

    const option = { ...this.displayedEntries[index] };
    const selectedEntryIndex = findSelectedEntryIndex(this.selectedEntries, option);

    this.selectedEntries.splice(selectedEntryIndex, 1);

    this.updateList();

    this.onSelect(this.selectedEntries);

  }

  handleOptionSelect(index) {

    const selectedOption = { ...this.displayedEntries[index] };

    this.hoveredIndex = -1;

    if (this.multiselect === true) {
      this.selectedEntries.push(selectedOption);
    } else {
      this.selectedEntries[0] = selectedOption;
    }

    this.updateList();

    this.onSelect(this.selectedEntries);

  }

  handleKeyDownEvent(event) {

    event.stopPropagation();

    const key = event.key;

    if (key === 'ArrowUp') {
      this.highlightPreviousOption();
    } else if (key === 'ArrowDown') {
      this.highlightNextOption();
    } else if (key === 'Enter') {
      this.handleOptionSelect(this.hoveredIndex);
    } else if (key === 'Escape') {
      this.onClose();
    }

    return false;

  }

  highlightPreviousOption() {

    if (this.hoveredIndex === -1) {
      this.hoveredIndex = this.displayedEntries.length - 1;
    } else if (this.hoveredIndex === 0) {
      this.hoveredIndex = this.displayedEntries.length - 1;
    } else {
      this.hoveredIndex--;
    }

    this.updateList();

  }

  highlightNextOption() {

    if (this.hoveredIndex === -1) {
      this.hoveredIndex = 0;
    } else if (this.hoveredIndex >= this.displayedEntries.length - 1) {
      this.hoveredIndex = 0;
    } else {
      this.hoveredIndex++;
    }

    this.updateList();

  }

  isSelectedOption(option) {

    return isPropertyValueExists(this.selectedEntries, 'key', option.key);

  }

  isHighlightedOption(index) {

    return this.hoveredIndex === index;

  }

  handleOptionHighlight(index) {

    this.hoveredIndex = index;
    this.updateList();

  }

  selectHighlightedOption() {

    const hoveredIndex = this.hoveredIndex;

    if (hoveredIndex === -1) {
      return;
    }

    const hoveredOption = { ...this.displayedEntries[hoveredIndex] };
    const selectedEntryIndex = findSelectedEntryIndex(this.selectedEntries, hoveredOption);

    if (selectedEntryIndex !== -1) {
      this.handleOptionUnselect(hoveredIndex);
    } else {
      this.handleOptionSelect(hoveredIndex);
    }

  }

  setWorking() {

    this.working = true;
    setChildren(this.el, this.spinner);

  }

  setIdle() {

    this.working = false;
    setChildren(this.el, this.spinner);

  }

  setEntries(entries) {

    this.entries = this.displayedEntries = entries;
    this.updateList();

  }

  setSelectedEntries(selectedEntries) {

    this.selectedEntries = selectedEntries;
    this.updateList();

  }

  getSelectedEntries() {

    return [...this.selectedEntries];

  }

  reset(options) {

    this.hoveredIndex = -1;
    this.entries = this.displayedEntries = options.entries;
    this.selectedEntries = options.selectedEntries;
    this.updateList();

  }

  filterEntries(labelText) {

    this.displayedEntries = filterByPropertyPartialValue(this.entries, 'label', labelText);
    const previousHoveredIndex = this.hoveredIndex;

    this.hoveredIndex = 0;
    this.updateList();

    if (this.displayedEntries.length === 0) {
      this.hoveredIndex = -1;
      this.showNoResultsFound();
    } else if (previousHoveredIndex === -1) {
      this.hideNoresultsFound();
    }
  }

  unsetFilter() {

    this.displayedEntries = this.entries;

    this.updateList();

  }

  showNoResultsFound() {

    if (this.preventNoResultDisplay === true) {
      return;
    }

    mount(this.el, this.noResultDisplay);

  }

  hideNoresultsFound() {

  }

  resetHighlighted() {

    if (this.entries.length === 0) {
      return;
    }

    this.hoveredIndex = -1;

    this.updateList();

  }

  updateList() {

    this.el.update(this.displayedEntries, this.context);

    if (this.skipScrollingToFocus === true) {
      return;
    }

    let hoveredEntry = null;

    if (this.hoveredIndex >= 0 && this.hoveredIndex < this.displayedEntries.length) {
      hoveredEntry = this.displayedEntries[this.hoveredIndex];
      const entry = this.el.lookup[hoveredEntry.key];
      entry.el.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }

  }

  clear() {

    this.selectedEntries = [];
    this.updateList();

  }

  configure(configuration) {

    this.entries = this.displayedEntries = configuration.entries;
    this.selectedEntries = configuration.selectedEntries;
    this.multiselect = configuration.multiselect || false;
    this.updateList();

  }

  getKeyEntry(key) {

    const entries = this.entries;

    for (let entry of entries) {

      if (entry.key === key) {
        return structuredClone(entry);
      }

    }

    return null;

  }

}

NavigableList.toEntries = function toEntries(collection, keyField, labelField) {

  const entries = [];

  for (let object of collection) {

    const entry = {
      key: object[keyField],
      label: object[labelField],
      data: object
    };

    entries.push(entry);

  }

  return entries;

};

export default NavigableList;